/*
 * Quotes (draft 3, pipeline merged in on 2026-10-28, reworked 2026-10-29 from the old Steward and Jobber): the top cards
 * (quotes by status, then the past 30 days next to the 30 before), the pipeline of leads by stage (each column scrolls and
 * has its own settings), the quote list, the quote builder (Jobber's client picker and New client box so the browser
 * fills the address, the auto quoter, add-ons, a deposit, how long it is good for), and one quote's page.
 */
import { Navigate, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import {
  addDays,
  CLIENT_TITLES,
  followUp,
  NO_STAGE_RULES,
  type ClientDetail,
  type HomeAnswers,
  type LawnAnswers,
  type WalkthroughRow,
  PRICE_OFTEN_WORDS,
  QUOTE_STATUS_WORDS,
  quoteMessage,
  quoteTotals,
  shortName,
  splitAddress,
  type LeadRow,
  type PriceOften,
  type QuickQuote,
  type QuoteDetail,
  type QuoteRow,
  type QuoteStats,
  type QuoteStatus,
  type StageRules,
} from "@steward/shared";
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type DragEvent, type SyntheticEvent } from "react";
import { useClient, useClients, useCreateClient, useTestMode } from "../api/hooks";
import {
  useArchiveQuote,
  useConvertQuote,
  useCreateLead,
  useCreateQuote,
  useDeclineQuote,
  useMoveLead,
  usePipeline,
  useQuote,
  useQuotes,
  useQuoteStats,
  useRequest,
  useRestoreQuote,
  useSendQuote,
  useStageRules,
  useUpdateQuote,
  type Via,
} from "../api/money";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Choice, Combobox, FormField, InputText } from "../components/fields";
import { clock, followUpText, money, moneyExact, phoneText, shortDate, todayIn, trend } from "../components/format";
import { markFresh } from "../components/next";
import { Menu, Modal, useToast } from "../components/overlays";
import { Icon } from "../components/Icon";
import { PhoneRecord } from "../components/PhoneRecord";
import { usePhone } from "../components/usePhone";
import {
  ago,
  Banner,
  Button,
  Card,
  Chip,
  Details,
  EmptyState,
  IconButton,
  LoadError,
  Page,
  Skeleton,
  StatusLabel,
  type Status,
} from "../components/parts";
import { draftLine, draftTotal, errText, LINE_OFTEN, LineMore, LinesEditor, linesOut, type DraftLine } from "../components/work";
import { EMPTY_PLACE, newClientMissing, PlaceFields, placeProperty, propertyOut, type PlaceDraft } from "./Clients";
import { LawnQuickPrice, QuickPrice } from "./QuickPrice";
import { QuoteSections, QuoteTop } from "./QuoteSections";
import { SentByThem } from "./Requests";
import { SettingsButton } from "./settings/Feature";

const QUOTE_LOOK: Record<QuoteStatus, Status> = {
  draft: "inactive",
  sent: "warning",
  approved: "success",
  converted: "informative",
  declined: "critical",
  expired: "inactive",
};
export const QuoteTag = ({ status }: { status: QuoteStatus }) => (
  <StatusLabel status={QUOTE_LOOK[status]}>{QUOTE_STATUS_WORDS[status]}</StatusLabel>
);
const tokenOf = (link: string) => link.split("/").pop() ?? "";
const DAY = 86_400_000;
/** A stored time as the day it was here (not in London), for shortDate. */
const localDay = (iso: string) => new Date(iso).toLocaleDateString("en-CA");
const sourceWords = (s: string | null) => (s ? s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : null);

/** The parts of the Quotes page a link can scroll to (the hash, as in /quotes#quotes-pipeline). */
export type QuotesView = "pipeline" | "list";
export const quotesHash = (v: QuotesView) => `quotes-${v}`;

type StatusPick = QuoteStatus | "all";

/**
 * Quotes on one page (owner, 2026-10-28): the top cards, then the pipeline board (leads by stage, each one a quote away),
 * then every quote. The Overview card's counts pick the list's status. Old links to the pipeline land here.
 */
export function QuotesPage() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [status, setStatus] = useState<StatusPick>("all");
  const leads = can("lead.read");
  return (
    <Page
      title="Quotes"
      moreActions={<SettingsButton to="/quotes/settings">Quote settings</SettingsButton>}
      primaryAction={
        can("quote.update") ? (
          <Button icon="plus" onClick={() => void navigate({ to: "/quotes/new", search: {} })}>
            New quote
          </Button>
        ) : null
      }
      secondaryAction={
        can("quote.update") && company.trade === "cleaning" ? (
          <Button type="secondary" onClick={() => void navigate({ to: "/proposals/new" })}>
            New proposal
          </Button>
        ) : null
      }
    >
      <QuoteTiles
        companyId={company.id}
        status={status}
        onStatus={(s) => {
          setStatus(s);
          document.getElementById(quotesHash("list"))?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />
      {leads ? (
        <div id={quotesHash("pipeline")} className="quotes-part">
          <Card
            title="Pipeline"
            action={
              can("lead.update") ? (
                <Button type="secondary" size="small" icon="plus" onClick={() => setAdding(true)}>
                  New lead
                </Button>
              ) : null
            }
          >
            <PipelineBoard />
          </Card>
        </div>
      ) : null}
      <div id={quotesHash("list")} className="quotes-part stack">
        <QuotesList companyId={company.id} status={status} setStatus={setStatus} />
      </div>
      <NewLead open={adding} onClose={() => setAdding(false)} />
    </Page>
  );
}

/**
 * The top cards (owner asked which are best, 2026-10-29). Jobber's four, which the old Steward copied: Overview (what needs
 * you now, by status, each one filters the list), Conversion rate, Sent and Converted for the past 30 days, each next to
 * the 30 days before so a slow month shows.
 */
function QuoteTiles({
  companyId,
  status,
  onStatus,
}: {
  companyId: string;
  status: StatusPick;
  onStatus: (s: StatusPick) => void;
}) {
  const stats = useQuoteStats(companyId);
  const s: QuoteStats | undefined = stats.data;
  const wait = <span className="skeleton inline" />;
  const sent = s ? trend(s.sent.count, s.sentBefore.count) : null;
  const won = s ? trend(s.converted.count, s.convertedBefore.count) : null;
  const rate = s?.rate != null && s.rateBefore !== null ? trend(s.rate, s.rateBefore, { points: true }) : null;
  return (
    <div className="tiles">
      <div className="tile">
        <span className="label">Overview</span>
        <div className="tile-counts">
          {(["draft", "sent", "approved", "expired"] as const).map((k) => (
            <Fragment key={k}>
              <button type="button" aria-pressed={status === k} onClick={() => onStatus(status === k ? "all" : k)}>
                <QuoteTag status={k} />
              </button>
              <span className="num">{s ? s.counts[k] : "-"}</span>
            </Fragment>
          ))}
        </div>
      </div>
      <div className="tile">
        <span className="label">Conversion rate</span>
        <span className="value">{s ? (s.rate === null ? "-" : `${String(s.rate)}%`) : wait}</span>
        <span className="hint">Past 30 days, sent quotes approved</span>
        {rate ? <span className={`hint ${rate.cls}`}>{rate.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">Sent</span>
        <span className="value">{s ? s.sent.count : wait}</span>
        <span className="hint">Past 30 days{s ? `, ${money(s.sent.cents)}` : ""}</span>
        {sent ? <span className={`hint ${sent.cls}`}>{sent.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">Converted</span>
        <span className="value">{s ? s.converted.count : wait}</span>
        <span className="hint">Past 30 days{s ? `, ${money(s.converted.cents)}` : ""}</span>
        {won ? <span className={`hint ${won.cls}`}>{won.text}</span> : null}
      </div>
    </div>
  );
}

const RANGES = [
  ["all", "Any time"],
  ["30", "Past 30 days"],
  ["90", "Past 90 days"],
  ["365", "Past year"],
] as const;
type Range = (typeof RANGES)[number][0];

function QuotesList({
  companyId,
  status,
  setStatus,
}: {
  companyId: string;
  status: StatusPick;
  setStatus: (s: StatusPick) => void;
}) {
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>("all");
  const list = useQuotes(companyId, status);
  const since = range === "all" ? 0 : Date.now() - Number(range) * DAY;
  const rows = (list.data?.quotes ?? []).filter((q) => Date.parse(q.createdAt) >= since);
  return (
    <DataTable<QuoteRow>
      title="All quotes"
      rows={rows}
      rowKey={(q) => q.id}
      loading={list.isFetching}
      error={list.error ? <LoadError error={list.error} retry={() => void list.refetch()} /> : null}
      onRowClick={(q) => void navigate({ to: "/quotes/$id", params: { id: q.id } })}
      search={(q) => [q.name, q.title, q.address, `#${String(q.number)}`].join(" ")}
      tools={
        <>
          <div className="chips">
            {(["all", "draft", "sent", "approved", "converted", "declined", "expired"] as const).map((s) => (
              <Chip key={s} pressed={status === s} onClick={() => setStatus(s)}>
                {s === "all" ? "All" : QUOTE_STATUS_WORDS[s]}
              </Chip>
            ))}
          </div>
          <div className="chips">
            {RANGES.map(([k, label]) => (
              <Chip key={k} pressed={range === k} onClick={() => setRange(k)}>
                {label}
              </Chip>
            ))}
          </div>
        </>
      }
      empty={
        <EmptyState icon="quotes" title="No quotes here">
          A quote shows the client the work and the price. They approve it with their name on their phone.
        </EmptyState>
      }
      columns={[
        {
          key: "name",
          header: "Client",
          sort: (q) => q.name.toLowerCase(),
          render: (q) => (
            <div className="name-cell">
              <b>{q.name}</b>
              <span className="muted">{q.title ?? "Quote"}</span>
            </div>
          ),
        },
        {
          key: "number",
          header: "Quote number",
          sort: (q) => q.number,
          render: (q) => <span className="num">#{q.number}</span>,
        },
        { key: "property", header: "Property", render: (q) => q.address ?? <span className="muted">Not given</span> },
        {
          key: "created",
          header: "Created",
          sort: (q) => q.createdAt,
          render: (q) => shortDate(localDay(q.createdAt)),
        },
        {
          key: "status",
          header: "Status",
          sort: (q) => q.status,
          render: (q) => (
            <span className="row">
              <QuoteTag status={q.status} />
              {q.viewed && q.status === "sent" ? <StatusLabel status="informative">Viewed</StatusLabel> : null}
            </span>
          ),
        },
        {
          key: "total",
          header: "Total",
          align: "right",
          sort: (q) => q.totalCents,
          render: (q) => <span className="num">{moneyExact(q.totalCents)}</span>,
        },
      ]}
    />
  );
}

// The builder ----------------------------------------------------------------------------------------------------------

interface QuoteDraftLine extends DraftLine {
  optional: boolean;
}

/** The picker's last choice: opens the New client box. */
export const NEW_CLIENT = "new";
const OFTEN_OF: Record<string, PriceOften> = { "one-time": "once", weekly: "weekly", biweekly: "biweekly", monthly: "monthly" };

/** A quote this form can change: no Good, Better, Best options or monthly retainers it would drop. */
export const formCanEdit = (q: QuoteDetail) =>
  !q.proposal && (q.status === "draft" || q.status === "sent") && q.lines.every((l) => !l.optionGroup && l.bill === "once");

/** New quote, or Edit with quoteId (Edit only for quotes formCanEdit allows). */
export function QuoteForm({ leadId, clientId, quoteId }: { leadId?: string; clientId?: string; quoteId?: string }) {
  const existing = useQuote(quoteId ?? "", Boolean(quoteId));
  if (quoteId && !existing.data)
    return <Page title="Edit quote">{existing.error ? <LoadError error={existing.error} /> : <Skeleton rows={6} />}</Page>;
  // A proposal edits on its own page; a quote this form would break (options, retainers, signed) opens to read.
  if (existing.data?.proposal) return <Navigate to="/proposals/$id" params={{ id: existing.data.id }} replace />;
  if (existing.data && !formCanEdit(existing.data))
    return <Navigate to="/quotes/$id" params={{ id: existing.data.id }} replace />;
  return (
    <QuoteFormBody
      {...(leadId ? { leadId } : {})}
      {...(clientId ? { clientId } : {})}
      {...(quoteId && existing.data ? { q: existing.data } : {})}
    />
  );
}

function QuoteFormBody({ leadId, clientId, q }: { leadId?: string; clientId?: string; q?: QuoteDetail }) {
  const { company } = useSession();
  const navigate = useNavigate();
  const today = todayIn(company.timezone);
  const clients = useClients(company.id, { status: "active", sort: "last_activity" });
  const pipe = usePipeline(company.id);
  const lead = pipe.data?.leads.find((l) => l.id === leadId);
  const create = useCreateQuote();
  const update = useUpdateQuote();
  const [who, setWho] = useState(q?.clientId ?? clientId ?? "");
  const picked = useClient(who);
  const [newClient, setNewClient] = useState<NewClientStart | null>(null);
  const [title, setTitle] = useState(q?.title ?? "");
  const [message, setMessage] = useState(q?.message ?? "");
  const [frequency, setFrequency] = useState(q?.frequency ?? "one-time");
  const [lines, setLines] = useState<QuoteDraftLine[]>(() =>
    q
      ? q.lines.map((l) => ({
          ...draftLine({
            description: l.description,
            quantity: l.quantity,
            unitPriceCents: l.unitPriceCents,
            note: l.note,
            link: l.link ?? null,
            frequency: "every",
          }),
          frequency: q.frequency !== "one-time" && !l.recurring ? "first" : "every",
          optional: l.optional,
        }))
      : [{ ...draftLine(), optional: false }],
  );
  const [dep, setDep] = useState<"none" | "percent" | "amount">(q?.deposit?.kind ?? "none");
  const [depValue, setDepValue] = useState(
    q?.deposit ? String(q.deposit.kind === "amount" ? q.deposit.value / 100 : q.deposit.value) : "",
  );
  const [goodFor, setGoodFor] = useState(
    q?.expiresOn ? String(Math.max(1, Math.round((Date.parse(q.expiresOn) - Date.parse(today)) / DAY))) : "30",
  );
  const [err, setErr] = useState<string | null>(null);
  const [filled, setFilled] = useState(false);
  // From a lead: a client already made for it is picked; otherwise the New client box opens with what they sent.
  if (lead && !filled && !q) {
    setFilled(true);
    setTitle(lead.service ?? "");
    if (lead.clientId) setWho(lead.clientId);
    else {
      const [first = "", ...rest] = lead.name.trim().split(/\s+/);
      setNewClient({
        firstName: first,
        lastName: rest.join(" "),
        phone: phoneText(lead.phone) ?? "",
        email: lead.email ?? "",
        ...leadPlace(lead),
        source: sourceWords(lead.source) ?? "",
      });
    }
  }
  const repeating = frequency !== "one-time";
  const required = lines.filter((l) => !l.optional);
  const extras = lines.filter((l) => l.optional);
  const firstPlace = picked.data?.properties[0];
  const size = firstPlace ? { beds: firstPlace.beds, baths: firstPlace.baths, sqft: firstPlace.sqft } : undefined;
  const [answers, setAnswers] = useState<HomeAnswers | LawnAnswers | null>(null);
  const applyQuick = (quick: QuickQuote, a: HomeAnswers | LawnAnswers | null) => {
    setAnswers(a);
    setTitle(quick.title);
    setFrequency(quick.frequency);
    setLines([
      ...quick.lines.map((l) => ({
        ...draftLine({
          description: l.description,
          quantity: 1,
          unitPriceCents: l.unitPriceCents,
          frequency: "every",
          note: l.note ?? null,
          link: l.link ?? null,
        }),
        frequency: l.recurring || quick.frequency === "one-time" ? "every" : "first",
        optional: false,
      })),
      ...extras,
    ]);
    setErr(null);
  };
  const save = () => {
    const out = linesOut(lines);
    if (typeof out === "string") {
      setErr(out);
      return;
    }
    if (!out.length) {
      setErr("A quote needs a line.");
      return;
    }
    if (!who && !q) {
      setErr("Pick a client, or make a new one at the bottom of the list.");
      return;
    }
    if (dep !== "none" && !(Number(depValue) > 0)) {
      setErr("Type the deposit, or pick No deposit.");
      return;
    }
    const days = Number(goodFor);
    if (goodFor.trim() && !(Number.isInteger(days) && days > 0 && days <= 365)) {
      setErr("Good for takes a number of days from 1 to 365.");
      return;
    }
    const kept = lines.filter((l) => l.description.trim() || l.price.trim());
    const body = {
      ...(who ? { clientId: who } : {}),
      title: title.trim() || null,
      message: message.trim() || null,
      frequency,
      expiresOn: goodFor.trim() ? addDays(today, days) : null,
      // Priced from the price list: the client can change rooms, how often and add-ons on the quote page.
      ...(answers ? { answers: { ...answers } } : {}),
      deposit:
        dep === "none" ? null : { kind: dep, value: dep === "amount" ? Math.round(Number(depValue) * 100) : Number(depValue) },
      lines: out.map((l, n) => ({
        description: l.description,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
        note: l.note ?? null,
        link: l.link ?? null,
        optional: kept[n]?.optional ?? false,
        recurring: repeating && kept[n]?.frequency !== "first",
      })),
    };
    const done = {
      onSuccess: (r: QuoteDetail) => void navigate({ to: "/quotes/$id", params: { id: r.id } }),
      onError: (e: unknown) => setErr(errText(e)),
    };
    if (q) update.mutate({ id: q.id, patch: body }, done);
    else create.mutate({ ...body, ...(leadId ? { leadId } : {}) }, done);
  };
  const preview = quoteTotals(
    required.map((l) => ({ amountCents: draftTotal([l]), optional: false, picked: true })),
    dep === "none"
      ? null
      : { kind: dep, value: dep === "amount" ? Math.round(Number(depValue) * 100) || 0 : Number(depValue) || 0 },
  );
  const setLine = (next: DraftLine[], optional: boolean) =>
    setLines([...(optional ? required : []), ...next.map((l) => ({ ...l, optional })), ...(optional ? [] : extras)]);
  const pickedRow = (clients.data?.items ?? []).find((c) => c.id === who);
  return (
    <Page title={q ? `Edit quote #${String(q.number)}` : "New quote"} className="pform">
      <Card title="Service for">
        <div className="stack">
          <Combobox
            label="Client"
            value={who}
            onChange={(v) => {
              if (v !== NEW_CLIENT) setWho(v);
            }}
            options={(clients.data?.items ?? []).map((c) => ({
              value: c.id,
              label: c.name,
              sub: [c.address, [c.email, phoneText(c.phone)].filter(Boolean).join(" · ")].filter(Boolean).join("\n"),
              tag: c.active ? "Active" : "Inactive",
            }))}
            last={{
              value: NEW_CLIENT,
              label: "+ Create new client",
              onPick: (typed) => {
                const [first = "", ...rest] = typed.split(/\s+/);
                setNewClient({ firstName: first, lastName: rest.join(" ") });
              },
            }}
          />
          {who && (pickedRow || picked.data) ? (
            <div className="picked-client">
              <div className="who muted small">
                {(pickedRow?.address ?? picked.data?.address) ? <span>{pickedRow?.address ?? picked.data?.address}</span> : null}
                <span>
                  {[phoneText(pickedRow?.phone ?? picked.data?.phone ?? null), pickedRow?.email ?? picked.data?.email]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
            </div>
          ) : !who && q ? (
            <p className="muted small">
              This quote is for {q.contactName ?? q.name}, who is not a client yet. Pick or make a client to link it.
            </p>
          ) : null}
        </div>
      </Card>
      {company.trade === "cleaning" ? <QuickPrice size={size} onUse={applyQuick} onHourly={(q) => applyQuick(q, null)} /> : null}
      {company.trade === "lawn" ? (
        <LawnQuickPrice
          sqft={firstPlace?.lawnSqft}
          address={pickedRow?.address ?? picked.data?.address ?? null}
          onUse={applyQuick}
        />
      ) : null}
      <Card title="The work">
        <div className="stack">
          <InputText
            label="Job title"
            value={title}
            placeholder="Move out clean, 3 bed 2 bath"
            onChange={(e) => setTitle(e.target.value)}
          />
          <InputText
            label="Message to the client"
            value={message}
            placeholder="Thanks for asking us. Here is what we would do."
            onChange={(e) => setMessage(e.target.value)}
          />
          <Choice
            label="How often"
            value={frequency}
            onChange={setFrequency}
            options={Object.entries(OFTEN_OF).map(([f, o]) => ({ value: f, label: PRICE_OFTEN_WORDS[o] }))}
          />
          <LinesEditor lines={required} onChange={(n) => setLine(n, false)} firstVisit={repeating} />
          {repeating ? (
            <p className="muted small">
              Every visit lines go on the job. First visit only lines are added to the first visit. The total is the first visit.
            </p>
          ) : null}
        </div>
      </Card>
      <Card title="Add-ons the client can pick">
        <p className="muted small">
          The client ticks the ones they want before they approve. They are not in the total until picked.
        </p>
        <LinesEditor lines={extras} onChange={(n) => setLine(n, true)} />
      </Card>
      <Card title="Deposit and how long it is good for">
        <div className="stack">
          <Choice<"none" | "percent" | "amount">
            label="Ask for a deposit when they approve"
            value={dep}
            onChange={setDep}
            options={[
              { value: "none", label: "No deposit" },
              { value: "percent", label: "Percent" },
              { value: "amount", label: "Amount" },
            ]}
          />
          {dep !== "none" ? (
            <InputText
              label={dep === "percent" ? "Percent of the total" : "Deposit amount"}
              inputMode="decimal"
              {...(dep === "percent" ? { suffix: "%" } : { prefix: "$" })}
              value={depValue}
              description={`About ${moneyExact(preview.depositCents)} of ${moneyExact(preview.totalCents)} before add-ons.`}
              onChange={(e) => setDepValue(e.target.value)}
            />
          ) : null}
          <InputText
            label="Good for"
            suffix="days"
            inputMode="numeric"
            value={goodFor}
            description={
              goodFor.trim() && Number(goodFor) > 0
                ? `The client can approve until ${shortDate(addDays(today, Number(goodFor) || 0))}. Leave it empty for no end.`
                : "No end. The client can approve any time."
            }
            onChange={(e) => setGoodFor(e.target.value)}
          />
        </div>
      </Card>
      {err ? <Banner type="error">{err}</Banner> : null}
      <div className="row end form-bar">
        <Button
          type="tertiary"
          onClick={() =>
            q ? void navigate({ to: "/quotes/$id", params: { id: q.id } }) : void navigate({ to: "/quotes", search: {} })
          }
        >
          Cancel
        </Button>
        <Button loading={create.isPending || update.isPending} onClick={save}>
          {q ? "Save changes" : "Save quote"}
        </Button>
      </div>
      <NewClientBox
        start={newClient}
        trade={company.trade}
        onClose={() => setNewClient(null)}
        onMade={(c) => {
          setNewClient(null);
          setWho(c.id);
          setErr(null);
        }}
      />
    </Page>
  );
}

// One quote ------------------------------------------------------------------------------------------------------------

export function QuotePage({ id }: { id: string }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const quote = useQuote(id);
  const decline = useDeclineQuote();
  const archive = useArchiveQuote();
  const restore = useRestoreQuote();
  const [open, setOpen] = useState<"send" | "convert" | null>(null);
  const phone = usePhone();
  const q = quote.data;
  if (quote.error)
    return (
      <Page title="Quote">
        <LoadError error={quote.error} retry={() => void quote.refetch()} />
      </Page>
    );
  if (!q)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const editable = can("quote.update");
  const closed = q.status === "converted" || q.status === "declined";
  const item = (label: string, run: () => void, close: () => void) => (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        close();
        run();
      }}
    >
      {label}
    </button>
  );
  const primary =
    q.status === "approved" && can("job.update") ? (
      <Button icon="jobs" onClick={() => setOpen("convert")}>
        Make into a job
      </Button>
    ) : q.status === "converted" && q.jobId ? (
      <Button icon="jobs" onClick={() => void navigate({ to: "/jobs/$id", params: { id: q.jobId ?? "" } })}>
        Open the job
      </Button>
    ) : editable && !closed && q.status !== "approved" ? (
      <Button icon="mail" onClick={() => setOpen("send")}>
        {q.status === "draft" ? "Send quote" : "Send again"}
      </Button>
    ) : null;
  const secondary =
    q.proposal && q.status === "draft" && editable ? (
      <Button type="secondary" onClick={() => void navigate({ to: "/proposals/$id", params: { id: q.id } })}>
        Edit proposal
      </Button>
    ) : editable && formCanEdit(q) ? (
      <Button type="secondary" icon="edit" onClick={() => void navigate({ to: "/quotes/$id/edit", params: { id: q.id } })}>
        Edit
      </Button>
    ) : null;
  const moreMenu = (
    <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
      {(close) => (
        <>
          {item(
            "Preview as the client sees it",
            () => void navigate({ to: "/quote/$token", params: { token: tokenOf(q.link) } }),
            close,
          )}
          {item(
            "Copy link",
            () => {
              void navigator.clipboard.writeText(q.link);
              toast("Quote link copied");
            },
            close,
          )}
          {editable && !closed
            ? item(
                "Decline work",
                () =>
                  decline.mutate(q.id, {
                    onSuccess: () => toast("Quote marked declined"),
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  }),
                close,
              )
            : null}
          {editable && q.status !== "converted"
            ? item(
                "Archive",
                () =>
                  archive.mutate(q.id, {
                    onSuccess: () => {
                      toast(`Quote #${String(q.number)} archived`, {
                        undo: () =>
                          restore.mutate(q.id, {
                            onSuccess: () => void navigate({ to: "/quotes/$id", params: { id: q.id } }),
                          }),
                      });
                      void navigate({ to: "/quotes", search: {} });
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  }),
                close,
              )
            : null}
        </>
      )}
    </Menu>
  );
  const signed = q.signature ? (
    <Banner type="success">
      Approved by <b>{q.signature.name}</b> on {shortDate(localDay(q.signature.at))}.{" "}
      {q.signature.drawing ? "They typed their name and signed by hand." : "They typed their name to sign."}
      {q.signature.drawing ? (
        <img className="sign-shown" src={q.signature.drawing} alt={`Signed by ${q.signature.name}`} />
      ) : null}
    </Banner>
  ) : null;
  const mainCards = (
    <>
      <Card title="Product and service" flush>
        <QuoteLines q={q} />
      </Card>
      <QuoteSections q={q} editable={editable} />
      {q.terms ? (
        <Card title="Terms">
          <p className="pre-wrap">{q.terms}</p>
        </Card>
      ) : null}
    </>
  );
  const detailsCard = (
    <Card title="Details">
      <Details
        rows={[
          ["Created", shortDate(localDay(q.createdAt))],
          ["Sent", q.sentAt ? shortDate(localDay(q.sentAt)) : "Not yet"],
          [
            "Opened",
            q.views
              ? `${String(q.views)} ${q.views === 1 ? "time" : "times"}, last ${q.lastViewedAt ? shortDate(localDay(q.lastViewedAt)) : ""}`
              : "Not yet",
          ],
          ["How often", q.frequency === "one-time" ? "One time" : PRICE_OFTEN_WORDS[OFTEN_OF[q.frequency] ?? "once"]],
          ["Card to approve", q.cardRequired ? "Needed" : "Not needed"],
          ["Good until", q.expiresOn ? shortDate(q.expiresOn) : "No end"],
          [
            "Deposit",
            q.deposit
              ? `${moneyExact(q.depositCents)}${q.deposit.kind === "percent" ? ` (${String(q.deposit.value)}%)` : ""}`
              : "None",
          ],
        ]}
      />
      {q.depositInvoiceId ? (
        <Button
          type="tertiary"
          size="small"
          icon="invoices"
          onClick={() => void navigate({ to: "/invoices/$id", params: { id: q.depositInvoiceId ?? "" } })}
        >
          Deposit invoice
        </Button>
      ) : null}
    </Card>
  );
  const sheets = (
    <>
      <SendQuote q={q} open={open === "send"} onClose={() => setOpen(null)} />
      <ConvertQuote q={q} open={open === "convert"} onClose={() => setOpen(null)} />
    </>
  );

  if (phone)
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/quotes", search: {} }) }}
        status={
          <>
            <QuoteTag status={q.status} />
            <span className="muted num">Quote #{String(q.number)}</span>
          </>
        }
        title={moneyExact(q.totalCents)}
        sub={
          q.clientId ? (
            <button
              type="button"
              className="link"
              onClick={() => void navigate({ to: "/clients/$id", params: { id: q.clientId ?? "" } })}
            >
              {q.name} <Icon name="chevronRight" size={16} />
            </button>
          ) : (
            q.name
          )
        }
        phone={q.contactPhone}
        email={q.contactEmail}
        facts={[
          ...(q.title ? [{ key: "what", icon: "quotes" as const, main: q.title }] : []),
          ...(q.address
            ? [{ key: "where", icon: "pin" as const, main: q.address, sub: "Directions", directions: q.address }]
            : []),
          {
            key: "sent",
            icon: "mail" as const,
            main: q.sentAt ? `Sent ${shortDate(localDay(q.sentAt))}` : "Not sent yet",
            sub: q.views ? `Opened ${String(q.views)} ${q.views === 1 ? "time" : "times"}` : q.sentAt ? "Not opened yet" : "",
          },
        ]}
        actions={
          <>
            {primary}
            {secondary}
            {moreMenu}
          </>
        }
      >
        {signed}
        {mainCards}
        {q.leadId ? <WhatTheySent leadId={q.leadId} /> : null}
        {detailsCard}
        <Walkthroughs quoteId={q.id} />
        <QuoteActivity q={q} />
        {sheets}
      </PhoneRecord>
    );

  return (
    <Page
      title={q.title ?? `Quote for ${q.name}`}
      titleMetaData={<QuoteTag status={q.status} />}
      primaryAction={primary}
      secondaryAction={secondary}
      moreActions={moreMenu}
    >
      <QuoteTop q={q} editable={editable} />
      {signed}
      <div className="home-grid">
        <div className="stack">{mainCards}</div>
        <div className="stack">
          {q.depositInvoiceId ? (
            <Button
              type="secondary"
              icon="invoices"
              onClick={() => void navigate({ to: "/invoices/$id", params: { id: q.depositInvoiceId ?? "" } })}
            >
              Deposit invoice
            </Button>
          ) : null}
          {q.leadId ? <WhatTheySent leadId={q.leadId} /> : null}
          <Walkthroughs quoteId={q.id} />
          <QuoteActivity q={q} />
        </div>
      </div>
      {sheets}
    </Page>
  );
}

/** The old Steward's "What they sent from the website": the request this quote came from. */
function WhatTheySent({ leadId }: { leadId: string }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const req = useRequest(leadId, can("lead.read"));
  const r = req.data;
  if (!r) return null;
  return (
    <Card
      title="What they sent"
      action={
        <Button type="tertiary" size="small" onClick={() => void navigate({ to: "/requests/$id", params: { id: r.id } })}>
          Open request
        </Button>
      }
    >
      <div className="stack">
        <SentByThem r={r} />
        <Details
          rows={[
            ["Service", r.service],
            ["Found us", sourceWords(r.source)],
          ]}
        />
      </div>
    </Card>
  );
}

/** What happened to the quote, newest first, from its own dates. */
function QuoteActivity({ q }: { q: QuoteDetail }) {
  const when = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  const events: [string, string | null][] = [
    ["Created", q.createdAt],
    ["Sent", q.sentAt],
    [q.views ? `Opened by the client ${q.views === 1 ? "once" : `${String(q.views)} times`}` : "", q.lastViewedAt],
    [q.signature ? `Approved by ${q.signature.name}` : q.approvedAt ? "Approved" : "", q.signature?.at ?? q.approvedAt],
    ...q.followUps.map((f): [string, string] => [followUpText(f), f.at]),
  ];
  const shown = events
    .filter((e): e is [string, string] => Boolean(e[0]) && Boolean(e[1]))
    .sort((a, b) => b[1].localeCompare(a[1]));
  return (
    <Card title="Activity">
      <ul className="activity-list">
        {q.status === "converted" ? (
          <li>
            <span>Made into a job</span>
          </li>
        ) : q.status === "declined" ? (
          <li>
            <span>Declined</span>
          </li>
        ) : null}
        {shown.map(([what, at], n) => (
          <li key={n}>
            <span>{what}</span>
            <span className="muted small num">{when(at)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function QuoteLines({ q }: { q: QuoteDetail }) {
  const chosen = q.lines.filter((l) => !l.optional || l.picked);
  const subtotal = chosen.reduce((s, l) => s + l.amountCents, 0);
  // A first visit with one-time work on top (a first cut of tall grass) costs more than each repeat visit after it.
  const each = chosen.filter((l) => l.recurring).reduce((s, l) => s + l.amountCents, 0);
  const repeat = q.frequency !== "one-time" && each > 0 && each < subtotal ? each : null;
  return (
    <table className="data lines">
      <thead className="lines-head">
        <tr>
          <th>Line item</th>
          <th className="right">Quantity</th>
          <th className="right">Unit price</th>
          <th className="right">Total</th>
        </tr>
      </thead>
      <tbody>
        {q.lines.map((l) => (
          <tr key={l.id} className={l.optional && !l.picked ? "inactive" : undefined}>
            <td>
              <b>{l.description}</b>
              {l.optional ? <span className="muted"> · Add-on{l.picked ? ", picked" : ""}</span> : null}
              {q.frequency !== "one-time" && !l.recurring ? <span className="muted"> · First visit only</span> : null}
              <LineMore line={l} basis={false} />
            </td>
            <td className="right num">{l.quantity}</td>
            <td className="right num muted">{moneyExact(l.unitPriceCents)}</td>
            <td className="right num">{moneyExact(l.amountCents)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3}>Subtotal</td>
          <td className="right num">{moneyExact(subtotal)}</td>
        </tr>
        {q.deposit ? (
          <tr>
            <td colSpan={3}>Deposit asked on approval</td>
            <td className="right num">{moneyExact(q.depositCents)}</td>
          </tr>
        ) : null}
        <tr>
          <td colSpan={3}>
            <b>{repeat ? "First visit total" : "Total"}</b>
          </td>
          <td className="right num">
            <b>{moneyExact(q.totalCents)}</b>
          </td>
        </tr>
        {repeat ? (
          <tr>
            <td colSpan={3}>Then {(LINE_OFTEN[q.frequency] ?? "each visit").toLowerCase()}</td>
            <td className="right num">{moneyExact(repeat)}</td>
          </tr>
        ) : null}
      </tfoot>
    </table>
  );
}

function SendQuote({ q, open, onClose }: { q: QuoteDetail; open: boolean; onClose: () => void }) {
  const { company } = useSession();
  const testMode = useTestMode(company.id);
  const send = useSendQuote();
  const toast = useToast();
  const [via, setVia] = useState<Via>(q.contactPhone ? "text" : "email");
  const [typed, setTyped] = useState<string | null>(null);
  const to = typed ?? (via === "text" ? q.contactPhone : q.contactEmail) ?? "";
  const first = (q.contactName ?? q.name).split(" ")[0] ?? "there";
  return (
    <Modal
      title="Send quote"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="mail"
            disabled={to.trim().length < 3}
            loading={send.isPending}
            onClick={() =>
              send.mutate(
                { id: q.id, via, to: to.trim() },
                {
                  onSuccess: () => {
                    toast(
                      testMode
                        ? `Quote saved for ${to.trim()}. Test mode, so it waits in the outbox.`
                        : `Quote sent to ${to.trim()}.`,
                    );
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Send {via === "text" ? "text" : "email"}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice<Via>
          label="Send by"
          value={via}
          onChange={(v) => {
            setVia(v);
            setTyped(null);
          }}
          options={[
            { value: "text", label: "Text" },
            { value: "email", label: "Email" },
          ]}
        />
        <InputText label="Send to" value={to} onChange={(e) => setTyped(e.target.value)} />
        <div className="field">
          <span className="label">The message</span>
          <div className="message-preview">{quoteMessage({ brand: q.brand || company.name, first, title: q.title }, q.link)}</div>
        </div>
        {testMode ? (
          <p className="muted small">Texts and emails are in test mode. This goes to the outbox and is not sent.</p>
        ) : null}
      </div>
    </Modal>
  );
}

function ConvertQuote({ q, open, onClose }: { q: QuoteDetail; open: boolean; onClose: () => void }) {
  const { company } = useSession();
  const convert = useConvertQuote();
  const navigate = useNavigate();
  const [date, setDate] = useState(todayIn(company.timezone));
  const [time, setTime] = useState("09:00");
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal
      title="Make into a job"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={convert.isPending}
            onClick={() =>
              convert.mutate(
                { id: q.id, startDate: date, startTime: time || null },
                {
                  onSuccess: (r) => {
                    markFresh(`job:${r.jobId}`);
                    void navigate({ to: "/jobs/$id", params: { id: r.jobId } });
                  },
                  onError: (e) => setErr(errText(e)),
                },
              )
            }
          >
            Make the job
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>The job gets the lines the client approved, {moneyExact(q.totalCents)} in all.</p>
        <div className="form-grid">
          <InputText label="First visit" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <InputText label="Start time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
        {err ? <Banner type="error">{err}</Banner> : null}
      </div>
    </Modal>
  );
}

// Pipeline -------------------------------------------------------------------------------------------------------------

/** Cards shown in a column before "Show more". The column also scrolls inside, so the board never runs down the page. */
const COLUMN_PAGE = 20;
/** Cards a column shows before the next one is cut by the fade (owner, 2026-10-29). The rest scroll. */
const SHOWN_CARDS = 3;

/** Fades the bottom of a column only while there is more below. */
function fadeList(list: HTMLElement) {
  list.classList.toggle("fade", list.scrollTop + list.clientHeight < list.scrollHeight - 4);
}

/** The column's settings in a few words under its name. */
function rulesWords(r: StageRules, stages: { id: string; name: string }[]): string {
  const to = stages.find((s) => s.id === r.moveToStageId)?.name;
  return [
    r.followUpDays ? `Follow up in ${String(r.followUpDays)} ${r.followUpDays === 1 ? "day" : "days"}` : null,
    r.moveAfterDays && to ? `To ${to} after ${String(r.moveAfterDays)} days` : null,
    r.hideAfterDays ? `Hides after ${String(r.hideAfterDays)} days` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function PipelineBoard() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [older, setOlder] = useState(false);
  const pipe = usePipeline(company.id, older);
  const move = useMoveLead();
  const [shown, setShown] = useState<Record<string, number>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  // Where a lead was just moved, shown at once while the board reloads.
  const [moved, setMoved] = useState<Record<string, string>>({});
  const board = useRef<HTMLDivElement>(null);
  const p = pipe.data;
  const editable = can("lead.update");
  useEffect(() => {
    if (!p) return;
    setMoved((m) => {
      const left = Object.entries(m).filter(([id, to]) => p.leads.some((l) => l.id === id && l.stageId !== to));
      return left.length === Object.keys(m).length ? m : Object.fromEntries(left);
    });
  }, [p]);
  // Each column is as tall as its first 3 cards plus part of the 4th, so the cut shows there is more to scroll.
  useLayoutEffect(() => {
    const el = board.current;
    if (!el) return;
    const fit = () => {
      for (const list of el.querySelectorAll<HTMLElement>(".stage-list")) {
        const next = list.querySelectorAll<HTMLElement>(":scope > .lead")[SHOWN_CARDS];
        list.style.maxHeight = next ? `${String(Math.round(next.offsetTop + next.offsetHeight * 0.45))}px` : "";
        fadeList(list);
      }
    };
    fit();
    const watch = new ResizeObserver(fit);
    watch.observe(el);
    return () => watch.disconnect();
  });
  if (pipe.error) return <LoadError error={pipe.error} retry={() => void pipe.refetch()} />;
  if (!p) return <Skeleton rows={5} />;
  const now = new Date();
  const hiddenTotal = Object.values(p.hidden ?? {}).reduce((s, n) => s + n, 0);
  const stage = p.stages.find((s) => s.id === editing);
  const leads = p.leads.map((l) => ({ ...l, stageId: moved[l.id] ?? l.stageId }));
  const dragged = leads.find((l) => l.id === dragId);
  const moveTo = (l: LeadRow, stageId: string) => {
    if (l.stageId === stageId) return;
    const from = l.stageId;
    setMoved((m) => ({ ...m, [l.id]: stageId }));
    move.mutate(
      { id: l.id, stageId },
      {
        onSuccess: () => toast(`${l.name} moved`, { undo: () => moveTo({ ...l, stageId }, from) }),
        onError: (e) => {
          setMoved((m) => ({ ...m, [l.id]: from }));
          toast(errText(e), { kind: "error" });
        },
      },
    );
  };
  const dropOn = (stageId: string) => ({
    onDragOver: (e: DragEvent) => {
      if (!dragged) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setOver(stageId);
    },
    onDragLeave: (e: DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver((o) => (o === stageId ? null : o));
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setOver(null);
      setDragId(null);
      if (dragged) moveTo(dragged, stageId);
    },
  });
  return (
    <>
      <div className="pipeline" ref={board}>
        {p.stages.map((s) => {
          const here = leads.filter((l) => l.stageId === s.id);
          const limit = shown[s.id] ?? COLUMN_PAGE;
          const rules = s.rules ?? NO_STAGE_RULES;
          const words = rulesWords(rules, p.stages);
          const hidden = p.hidden?.[s.id] ?? 0;
          return (
            <section
              className={`stage${over === s.id && dragged && dragged.stageId !== s.id ? " over" : ""}`}
              key={s.id}
              aria-label={s.name}
              {...dropOn(s.id)}
            >
              <div className="stage-head">
                <div className="stage-name">
                  <b>{s.name}</b>
                  <span className="muted num small">
                    {here.length} · {money(here.reduce((t, l) => t + (l.quote?.totalCents ?? l.valueCents ?? 0), 0))}
                  </span>
                  {words ? <span className="stage-rules">{words}</span> : null}
                </div>
                {editable ? (
                  <IconButton icon="settings" label={`${s.name} column settings`} size={18} onClick={() => setEditing(s.id)} />
                ) : null}
              </div>
              <div className="stage-list" onScroll={(e) => fadeList(e.currentTarget)}>
                {here.length ? (
                  here.slice(0, limit).map((l) => (
                    <LeadCard
                      key={l.id}
                      lead={l}
                      rules={rules}
                      now={now}
                      stages={p.stages}
                      editable={editable}
                      dragging={dragId === l.id}
                      onDrag={(on) => {
                        setDragId(on ? l.id : null);
                        if (!on) setOver(null);
                      }}
                      onMove={(stageId) => moveTo(l, stageId)}
                      onQuote={() =>
                        l.quote
                          ? void navigate({ to: "/quotes/$id", params: { id: l.quote.id } })
                          : void navigate({ to: "/quotes/new", search: { leadId: l.id } })
                      }
                      onOpen={() => void navigate({ to: "/requests/$id", params: { id: l.id } })}
                    />
                  ))
                ) : (
                  <p className="muted small stage-empty">Nobody here</p>
                )}
                {here.length > limit ? (
                  <Button type="tertiary" size="small" onClick={() => setShown({ ...shown, [s.id]: limit + COLUMN_PAGE })}>
                    Show {Math.min(COLUMN_PAGE, here.length - limit)} more
                  </Button>
                ) : null}
              </div>
              {hidden ? (
                <span className="muted small">
                  {hidden} older {hidden === 1 ? "lead is" : "leads are"} hidden
                </span>
              ) : null}
            </section>
          );
        })}
      </div>
      {hiddenTotal || older ? (
        <div className="pipeline-foot">
          <span className="muted small">
            {older
              ? "Showing older leads too."
              : `${String(hiddenTotal)} older ${hiddenTotal === 1 ? "lead is" : "leads are"} off the board by column settings. They are kept.`}
          </span>
          <Button type="tertiary" size="small" onClick={() => setOlder(!older)}>
            {older ? "Hide older leads" : "Show older leads"}
          </Button>
        </div>
      ) : null}
      {stage ? (
        <StageSettings
          key={stage.id}
          stage={{ id: stage.id, name: stage.name, rules: stage.rules ?? NO_STAGE_RULES }}
          stages={p.stages}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

/** One column's own settings, saved for the company. */
function StageSettings({
  stage,
  stages,
  onClose,
}: {
  stage: { id: string; name: string; rules: StageRules };
  stages: { id: string; name: string }[];
  onClose: () => void;
}) {
  const save = useStageRules();
  const toast = useToast();
  const r = stage.rules;
  const txt = (n: number | null) => (n === null ? "" : String(n));
  const [hide, setHide] = useState(txt(r.hideAfterDays));
  const [moveAfter, setMoveAfter] = useState(txt(r.moveAfterDays));
  const [moveTo, setMoveTo] = useState(r.moveToStageId ?? "");
  const [follow, setFollow] = useState(txt(r.followUpDays));
  const [err, setErr] = useState<string | null>(null);
  const others = stages.filter((s) => s.id !== stage.id);
  const days = (s: string, what: string): number | null | string => {
    if (!s.trim()) return null;
    const n = Number(s);
    return Number.isInteger(n) && n >= 1 && n <= 3650 ? n : `${what} takes a number of days from 1 to 3650, or leave it empty.`;
  };
  const go = () => {
    const h = days(hide, "Hide after");
    const m = days(moveAfter, "Move after");
    const f = days(follow, "Follow up");
    const bad = [h, m, f].find((x) => typeof x === "string");
    if (typeof bad === "string") {
      setErr(bad);
      return;
    }
    if ((m === null) !== !moveTo) {
      setErr("To move leads on, give the days and pick the column. Or clear both.");
      return;
    }
    save.mutate(
      {
        id: stage.id,
        rules: {
          hideAfterDays: h as number | null,
          moveAfterDays: m as number | null,
          moveToStageId: moveTo || null,
          followUpDays: f as number | null,
        },
      },
      {
        onSuccess: () => {
          toast(`${stage.name} column settings saved`);
          onClose();
        },
        onError: (e) => setErr(errText(e)),
      },
    );
  };
  return (
    <Modal
      title={`${stage.name} column settings`}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={go}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">These are for this column only and for everyone in this company. Empty means off.</p>
        <InputText
          label="Follow up after"
          suffix="days"
          inputMode="numeric"
          value={follow}
          description="Cards show when to reach out. Due today turns yellow, 2 days late turns red."
          onChange={(e) => setFollow(e.target.value)}
        />
        <div className="form-grid">
          <InputText
            label="Move them on after"
            suffix="days"
            inputMode="numeric"
            value={moveAfter}
            onChange={(e) => setMoveAfter(e.target.value)}
          />
          <FormField label="Move them to">
            <select className="select" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
              <option value="">Pick a column</option>
              {others.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
        </div>
        <p className="muted small">Checked every 15 minutes. A moved lead starts its time in the new column.</p>
        <InputText
          label="Hide from the board after"
          suffix="days"
          inputMode="numeric"
          value={hide}
          description="Old leads drop off this column so it stays short. They are kept, and Show older leads brings them back."
          onChange={(e) => setHide(e.target.value)}
        />
        {err ? <Banner type="error">{err}</Banner> : null}
      </div>
    </Modal>
  );
}

function LeadCard({
  lead,
  rules,
  now,
  stages,
  editable,
  dragging,
  onDrag,
  onMove,
  onQuote,
  onOpen,
}: {
  lead: LeadRow;
  rules: StageRules;
  now: Date;
  stages: { id: string; name: string }[];
  editable: boolean;
  dragging: boolean;
  onDrag: (on: boolean) => void;
  onMove: (stageId: string) => void;
  onQuote: () => void;
  onOpen: () => void;
}) {
  const f = followUp(rules, lead.stageChangedAt, now);
  const contact = [phoneText(lead.phone), lead.email].filter(Boolean).join(" · ");
  const [first = "", ...rest] = (lead.assignedName ?? "").split(/\s+/);
  const extra = [
    sourceWords(lead.source),
    lead.assignedName ? `With ${shortName(first, rest.join(" "))}` : null,
    ago(lead.stageChangedAt),
  ]
    .filter(Boolean)
    .join(" · ");
  const followText = f
    ? f.daysLate < 0
      ? `Follow up ${shortDate(f.due.toLocaleDateString("en-CA"))}`
      : f.daysLate === 0
        ? "Follow up today"
        : `Follow up was due ${String(f.daysLate)} ${f.daysLate === 1 ? "day" : "days"} ago`
    : null;
  return (
    <div
      className={`lead${f?.look === "critical" ? " late" : ""}${dragging ? " dragging" : ""}`}
      draggable={editable}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", lead.id);
        e.dataTransfer.effectAllowed = "move";
        onDrag(true);
      }}
      onDragEnd={() => onDrag(false)}
    >
      <div className="row between">
        <b>{lead.businessName ?? lead.name}</b>
        <Menu
          align="right"
          button={({ toggle }) => <IconButton icon="more" label={`More for ${lead.name}`} size={18} onClick={toggle} />}
        >
          {(close) => (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  onOpen();
                }}
              >
                Open request
              </button>
              {editable
                ? stages
                    .filter((s) => s.id !== lead.stageId)
                    .map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          close();
                          onMove(s.id);
                        }}
                      >
                        Move to {s.name}
                      </button>
                    ))
                : null}
            </>
          )}
        </Menu>
      </div>
      {lead.businessName ? <span className="lead-line">{lead.name}</span> : null}
      <span className="lead-line">
        {lead.service ?? "No service yet"}
        {!lead.quote && lead.valueCents ? ` · ${money(lead.valueCents)}` : ""}
      </span>
      {lead.quote ? (
        <button type="button" className="lead-quote" onClick={onQuote}>
          <b className="num">Quote #{lead.quote.number}</b>
          <QuoteTag status={lead.quote.status} />
          <span className="num">{moneyExact(lead.quote.totalCents)}</span>
        </button>
      ) : null}
      {contact ? <span className="lead-line">{contact}</span> : null}
      {lead.address ? <span className="lead-line">{lead.address}</span> : null}
      <span className="lead-line">{extra}</span>
      {followText ? <span className={`lead-line lead-follow ${f?.look ?? ""}`}>{followText}</span> : null}
      {editable && !lead.quote ? (
        <Button type="tertiary" size="small" icon="quotes" onClick={onQuote}>
          Make a quote
        </Button>
      ) : null}
    </div>
  );
}

function NewLead({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateLead();
  const toast = useToast();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [value, setValue] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const go = () => {
    if (!name.trim()) {
      setErr("Type a name.");
      return;
    }
    const cents = Math.round(Number(value.replace(/[$,]/g, "")) * 100);
    create.mutate(
      {
        name: name.trim(),
        phone: phone.trim() || null,
        service: service.trim() || null,
        valueCents: cents > 0 ? cents : null,
        source: "Typed in",
      },
      {
        onSuccess: () => {
          toast(`${name.trim()} added to New`);
          setName("");
          setPhone("");
          setService("");
          setValue("");
          setErr(null);
          onClose();
        },
        onError: (e) => setErr(errText(e)),
      },
    );
  };
  return (
    <Modal
      title="New lead"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={create.isPending} onClick={go}>
            Add lead
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <InputText label="Name" value={name} error={err} onChange={(e) => setName(e.target.value)} />
        <InputText label="Mobile phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <InputText label="Service they want" value={service} onChange={(e) => setService(e.target.value)} />
        <InputText
          label="About how much"
          prefix="$"
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      <p className="muted small">The lead goes into New. Move it along as you talk with them.</p>
    </Modal>
  );
}

/** Notes and photos from a walkthrough before the bid (FEATURE-MAP 9). */
function Walkthroughs({ quoteId }: { quoteId: string }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["walkthroughs", quoteId],
    queryFn: () => api<{ walkthroughs: WalkthroughRow[] }>("GET", `walkthroughs?quoteId=${quoteId}`),
  });
  const [notes, setNotes] = useState("");
  const [open, setOpen] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["walkthroughs", quoteId] });
  const add = async () => {
    await api("POST", "walkthroughs", { quoteId, day: new Date().toLocaleDateString("en-CA"), notes });
    setNotes("");
    setOpen(false);
    await refresh();
  };
  const photo = (id: string, f: File) => {
    const r = new FileReader();
    r.onload = () => {
      const data = (typeof r.result === "string" ? r.result : "").split(",")[1] ?? "";
      void api("POST", `walkthroughs/${id}/photos`, { name: f.name, mime: f.type || "image/jpeg", dataBase64: data }).then(
        refresh,
      );
    };
    r.readAsDataURL(f);
  };
  return (
    <Card
      title="Walkthrough"
      action={
        !open ? (
          <Button type="tertiary" size="small" icon="plus" onClick={() => setOpen(true)}>
            Add notes
          </Button>
        ) : null
      }
    >
      {open ? (
        <div className="stack">
          <label className="field wide">
            <span className="label">What you saw (rooms, floors, special asks)</span>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <div className="row end">
            <Button type="tertiary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!notes.trim()} onClick={() => void add()}>
              Save
            </Button>
          </div>
        </div>
      ) : null}
      {(list.data?.walkthroughs ?? []).map((w) => (
        <div key={w.id} className="stack">
          <b>
            {shortDate(w.day)}
            {w.time ? ` at ${clock(w.time)}` : null}
            {w.who ? ` · ${w.who}` : null}
          </b>
          {w.notes ? <p>{w.notes}</p> : null}
          <span className="muted small">
            {w.photos.length} {w.photos.length === 1 ? "photo" : "photos"}
          </span>
          <label className="btn tertiary small">
            Add a photo
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) photo(w.id, f);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      ))}
      {!open && !list.data?.walkthroughs.length ? <p className="muted">No walkthrough yet.</p> : null}
    </Card>
  );
}

// New client box (Jobber's) ----------------------------------------------------------------------------------------------

/** What the New client box opens with: typed in the picker, or what a lead sent. */
export interface NewClientStart {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  source?: string;
}

/** A request's address for the New client box, in parts: saved parts, else an older one line address split up. */
function leadPlace(lead: LeadRow): Pick<NewClientStart, "street" | "street2" | "city" | "state" | "zip"> {
  const p = lead.place.city ? lead.place : splitAddress(lead.address);
  return {
    street: p.street1 ?? lead.address ?? "",
    street2: p.street2 ?? "",
    ...(p.city ? { city: p.city } : {}),
    ...(p.state ? { state: p.state } : {}),
    zip: p.zip ?? "",
  };
}

/**
 * Jobber's New client box (owner, 2026-10-29: "more like Jobber's so auto fill works"). One field per thing with the
 * browser's autocomplete names, inside a real form, so Chrome fills the name, phone, email and the whole address at once.
 * Saving makes the client right away, as Jobber does, and picks them on the quote.
 */
export function NewClientBox({
  start,
  trade,
  onClose,
  onMade,
}: {
  start: NewClientStart | null;
  trade: string;
  onClose: () => void;
  onMade: (c: ClientDetail) => void;
}) {
  if (!start) return null;
  return <NewClientForm start={start} trade={trade} onClose={onClose} onMade={onMade} />;
}

function NewClientForm({
  start,
  trade,
  onClose,
  onMade,
}: {
  start: NewClientStart;
  trade: string;
  onClose: () => void;
  onMade: (c: ClientDetail) => void;
}) {
  const create = useCreateClient();
  const [f, setF] = useState({
    title: "",
    firstName: start.firstName ?? "",
    lastName: start.lastName ?? "",
    company: "",
    phone: start.phone ?? "",
    email: start.email ?? "",
    source: start.source ?? "",
  });
  const [where, setWhere] = useState<PlaceDraft>({
    ...EMPTY_PLACE,
    street1: start.street ?? "",
    street2: start.street2 ?? "",
    city: start.city ?? EMPTY_PLACE.city,
    state: start.state ?? EMPTY_PLACE.state,
    zip: start.zip ?? "",
  });
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  const business = Boolean(f.company.trim());
  const place = placeProperty(where);
  const save = (e?: SyntheticEvent) => {
    e?.preventDefault();
    const missing = newClientMissing({ firstName: f.firstName, phone: f.phone }, { ...place, address: where.street1 });
    if (missing) {
      setErr(missing);
      return;
    }
    create.mutate(
      {
        ...(f.title ? { title: f.title as (typeof CLIENT_TITLES)[number] } : {}),
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim() || null,
        companyName: f.company.trim() || null,
        phone: f.phone.trim(),
        email: f.email.trim() || null,
        leadSource: f.source.trim() || null,
        property: propertyOut(place),
      },
      { onSuccess: onMade, onError: (x) => setErr(errText(x)) },
    );
  };
  return (
    <Modal
      title="New client"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={create.isPending} onClick={() => save()}>
            Save client
          </Button>
        </>
      }
    >
      <form className="client-form" autoComplete="on" onSubmit={save} aria-label="New client">
        <div className="span-2 wide-phone">
          <FormField label="Title">
            <select className="select" name="honorific-prefix" autoComplete="honorific-prefix" {...set("title")}>
              <option value="">No title</option>
              {CLIENT_TITLES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </FormField>
        </div>
        <div className="span-2 wide-phone">
          <InputText label="First name" name="given-name" autoComplete="given-name" required {...set("firstName")} />
        </div>
        <div className="span-2 wide-phone">
          <InputText label="Last name" name="family-name" autoComplete="family-name" {...set("lastName")} />
        </div>
        <div className="span-6">
          <InputText
            label="Company name"
            name="organization"
            autoComplete="organization"
            description="Only for a business."
            {...set("company")}
          />
        </div>
        <div className="span-3 wide-phone">
          <InputText label="Phone number" type="tel" name="tel" autoComplete="tel" required {...set("phone")} />
        </div>
        <div className="span-3 wide-phone">
          <InputText label="Email" type="email" name="email" autoComplete="email" {...set("email")} />
        </div>
        <div className="span-6">
          <InputText
            label="Lead source"
            name="lead-source"
            autoComplete="off"
            placeholder="Google, a friend, Facebook"
            {...set("source")}
          />
        </div>
        <PlaceFields p={where} set={setWhere} trade={trade} business={business} />
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        {err ? (
          <div className="span-6">
            <Banner type="error">{err}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}
